'use client'

import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react'
import { User, Session, AuthError } from '@supabase/supabase-js'
import { supabase } from '../../lib/supabase'
import {
  isSessionExpired,
  isUserIdle,
  shouldShowIdleWarning,
  getTimeUntilIdleTimeout,
  updateLastActivity,
  initializeSession,
  clearSessionData,
  validateDeviceFingerprint,
  getRemainingSessionTime,
  formatTimeRemaining,
  formatSessionAge,
} from '../../lib/sessionSecurity'

interface SessionInfo {
  sessionAge: string
  timeUntilExpiry: string
  isExpiringSoon: boolean
}

interface SupabaseContextType {
  supabase: typeof supabase
  user: User | null
  session: Session | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<{
    data: { user: User | null; session: Session | null }
    error: AuthError | null
  }>
  signOut: () => Promise<{ error: AuthError | null }>
  // Security features
  showIdleWarning: boolean
  idleTimeRemaining: string
  sessionInfo: SessionInfo | null
  extendSession: () => void
  securityLogout: (reason: string) => Promise<void>
}

const SupabaseContext = createContext<SupabaseContextType>({} as SupabaseContextType)

interface SupabaseProviderProps {
  children: ReactNode
}

export const SupabaseProvider = ({ children }: SupabaseProviderProps) => {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [showIdleWarning, setShowIdleWarning] = useState(false)
  const [idleTimeRemaining, setIdleTimeRemaining] = useState('')
  const [sessionInfo, setSessionInfo] = useState<SessionInfo | null>(null)

  // Security logout with reason logging
  const securityLogout = useCallback(async (reason: string) => {
    if (import.meta.env.DEV) {
      console.warn(`[Security] Logging out user: ${reason}`)
    }
    clearSessionData()
    await supabase.auth.signOut()
    setShowIdleWarning(false)
  }, [])

  // Extend session by updating activity
  const extendSession = useCallback(() => {
    updateLastActivity()
    setShowIdleWarning(false)
  }, [])

  // Check session security
  const checkSessionSecurity = useCallback(async () => {
    if (!user) return

    // Check for session expiration (max age)
    if (isSessionExpired()) {
      await securityLogout('Session exceeded maximum age (7 days)')
      return
    }

    // Check device fingerprint (async)
    const fingerprintValid = await validateDeviceFingerprint()
    if (!fingerprintValid) {
      await securityLogout('Device fingerprint mismatch - possible session theft')
      return
    }

    // Check for idle timeout
    if (isUserIdle()) {
      await securityLogout('User idle timeout (30 minutes)')
      return
    }

    // Check if idle warning should be shown
    if (shouldShowIdleWarning()) {
      setShowIdleWarning(true)
      const remaining = getTimeUntilIdleTimeout()
      setIdleTimeRemaining(formatTimeRemaining(remaining))
    } else {
      setShowIdleWarning(false)
    }

    // Update session info
    const remaining = getRemainingSessionTime()
    const expiryHours = remaining / (60 * 60 * 1000)
    setSessionInfo({
      sessionAge: formatSessionAge(),
      timeUntilExpiry: formatTimeRemaining(remaining),
      isExpiringSoon: expiryHours < 24, // Less than 24 hours
    })
  }, [user, securityLogout])

  // Activity tracking - update on user interaction
  useEffect(() => {
    if (!user) return

    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart']

    // Throttle activity updates to avoid excessive writes
    let lastUpdate = 0
    const throttledHandler = () => {
      const now = Date.now()
      if (now - lastUpdate > 10000) { // Update at most every 10 seconds
        lastUpdate = now
        updateLastActivity()
        if (showIdleWarning) {
          setShowIdleWarning(false)
        }
      }
    }

    activityEvents.forEach(event => {
      window.addEventListener(event, throttledHandler, { passive: true })
    })

    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, throttledHandler)
      })
    }
  }, [user, showIdleWarning])

  // Security check interval
  useEffect(() => {
    if (!user) return

    // Initial check
    checkSessionSecurity()

    // Check every 30 seconds
    const interval = setInterval(checkSessionSecurity, 30000)

    return () => clearInterval(interval)
  }, [user, checkSessionSecurity])

  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      const { data: { session } } = await supabase.auth.getSession()

      if (session) {
        // Validate security before accepting session
        if (isSessionExpired()) {
          if (import.meta.env.DEV) {
            console.warn('[Security] Existing session expired, clearing')
          }
          clearSessionData()
          await supabase.auth.signOut()
          setSession(null)
          setUser(null)
          setLoading(false)
          return
        }

        const fingerprintValid = await validateDeviceFingerprint()
        if (!fingerprintValid) {
          if (import.meta.env.DEV) {
            console.warn('[Security] Device fingerprint mismatch, clearing session')
          }
          clearSessionData()
          await supabase.auth.signOut()
          setSession(null)
          setUser(null)
          setLoading(false)
          return
        }

        // Session is valid, update activity
        updateLastActivity()
      }

      setSession(session)
      setUser(session?.user ?? null)
      setLoading(false)
    }

    getInitialSession()

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN' && session) {
          // Initialize security tracking for new sign in
          await initializeSession()
        } else if (event === 'SIGNED_OUT') {
          // Clear security data on sign out
          clearSessionData()
          setShowIdleWarning(false)
          setSessionInfo(null)
        }

        setSession(session)
        setUser(session?.user ?? null)
        setLoading(false)
      }
    )

    return () => subscription.unsubscribe()
  }, [])

  const signIn = async (email: string, password: string) => {
    const result = await supabase.auth.signInWithPassword({
      email,
      password
    })

    if (result.data.session) {
      // Initialize session security on successful login
      await initializeSession()
    }

    return result
  }

  const signOut = async () => {
    clearSessionData()
    setShowIdleWarning(false)
    setSessionInfo(null)
    return await supabase.auth.signOut()
  }

  const value: SupabaseContextType = {
    supabase,
    user,
    session,
    loading,
    signIn,
    signOut,
    // Security features
    showIdleWarning,
    idleTimeRemaining,
    sessionInfo,
    extendSession,
    securityLogout,
  }

  return (
    <SupabaseContext.Provider value={value}>
      {children}
    </SupabaseContext.Provider>
  )
}

export const useSupabase = (): SupabaseContextType => {
  const context = useContext(SupabaseContext)
  if (context === undefined) {
    throw new Error('useSupabase must be used within a SupabaseProvider')
  }
  return context
}
